// Indian trading system: GST once per invoice, under the stone table.
// CGST % + SGST % (same state) or IGST % (another state), never both:
// typing on one side locks the other until it is cleared.

import type { ReactNode } from 'react';
import { invoiceGst, gstPercentsOf, type GstInput, type GstPercents } from '../utils/tradingSystem';

const toNum = (v: string) => { const n = parseFloat(v); return n > 0 ? n : undefined; };

export function GstBoxes({ value, onChange }: { value: GstInput; onChange: (v: GstInput) => void }) {
  const splitUsed = !!toNum(value.cgst) || !!toNum(value.sgst);
  const igstUsed  = !!toNum(value.igst);
  const box = (key: keyof GstInput, label: string, locked: boolean, title: string) => (
    <label className="gst-box" title={locked ? title : undefined}>
      <span>{label}</span>
      <input
        className="no-spinner" type="number" step="0.01" min="0" max="99" placeholder={locked ? 'n/a' : '0'}
        disabled={locked}
        value={locked ? '' : value[key]}
        onChange={e => onChange({ ...value, [key]: e.target.value })}
      />
    </label>
  );
  return (
    <div className="gst-row">
      <span className="gst-row-label">GST</span>
      {box('cgst', 'CGST %', igstUsed, 'Not used with IGST. Clear IGST to use CGST and SGST.')}
      {box('sgst', 'SGST %', igstUsed, 'Not used with IGST. Clear IGST to use CGST and SGST.')}
      <span className="gst-or">or</span>
      {box('igst', 'IGST %', splitUsed, 'Not used with CGST and SGST. Clear them to use IGST.')}
    </div>
  );
}

/**
 * Read-only GST summary for a saved invoice (sale / purchase popups):
 * Amount · CGST · SGST (or IGST) · Total Amount. Nothing when it has no GST.
 */
export function GstSummary({ inv, money }: {
  inv: { taxableUSD?: number | null; cgstUSD?: number | null; sgstUSD?: number | null; igstUSD?: number | null; taxTotalUSD?: number | null;
    gst?: GstPercents | null; totalAmountUSD?: number };
  money: (v: number) => string;
}) {
  if (!inv.taxTotalUSD) return null;
  const pct = (v?: number | null) => (v ? ` ${v}%` : '');
  const items: [string, ReactNode][] = [
    ['Amount', money(inv.taxableUSD ?? 0)],
    ...((inv.cgstUSD ?? 0) > 0 ? [[`CGST${pct(inv.gst?.cgstPct)}`, money(inv.cgstUSD!)] as [string, ReactNode]] : []),
    ...((inv.sgstUSD ?? 0) > 0 ? [[`SGST${pct(inv.gst?.sgstPct)}`, money(inv.sgstUSD!)] as [string, ReactNode]] : []),
    ...((inv.igstUSD ?? 0) > 0 ? [[`IGST${pct(inv.gst?.igstPct)}`, money(inv.igstUSD!)] as [string, ReactNode]] : []),
    ['Total GST', money(inv.taxTotalUSD)],
  ];
  return (
    <div className="gst-summary">
      {items.map(([label, value]) => (
        <span key={label}><em>{label}</em><strong>{value}</strong></span>
      ))}
    </div>
  );
}

/**
 * GST on something we pay (an expense, a bank expense, a broker payout):
 * the GST boxes, a "not claimable" tick, and the amount with GST.
 * `base` is the amount before GST.
 */
export function PaidGstFields({ base, value, onChange, notClaimable, onNotClaimable, reverseCharge, onReverseCharge, money }: {
  base: number;
  value: GstInput; onChange: (v: GstInput) => void;
  notClaimable: boolean; onNotClaimable: (v: boolean) => void;
  /** Reverse charge: the seller charges no GST, we pay it to the government ourselves. */
  reverseCharge?: boolean; onReverseCharge?: (v: boolean) => void;
  money: (v: number) => string;
}) {
  const total = invoiceGst(base, gstPercentsOf(value)).total;
  return (
    <div className="paid-gst">
      <GstBoxes value={value} onChange={onChange} />
      <div className="paid-gst-foot">
        <label title="Tick when this GST can't be claimed back (e.g. the seller has no GSTIN). Then the whole amount is the expense.">
          <input type="checkbox" checked={notClaimable} onChange={e => onNotClaimable(e.target.checked)} />
          GST not claimable
        </label>
        {onReverseCharge && (
          <label style={{ marginRight: 0 }} title="The seller charges no GST. You pay it to the government yourself (and normally claim it back).">
            <input type="checkbox" checked={!!reverseCharge} onChange={e => onReverseCharge(e.target.checked)} />
            Reverse charge
          </label>
        )}
        <span>GST <strong>{money(total)}</strong>{reverseCharge ? ' (you pay it)' : ''}</span>
        <span>{reverseCharge ? 'Paid to seller' : 'Total paid'} <strong>{money(reverseCharge ? base : base + total)}</strong></span>
      </div>
    </div>
  );
}
